'use client'

import {
  Field,
  FieldContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui/components/start'
import { ComponentProps, createContext, ReactNode, useContext, useState } from 'react'

const ReadOnlyContext = createContext(false)

/**
 * Renders every opted-in editor below as a Read-only field. Scopes only add read-only: a nested
 * `when={false}` never makes a field editable inside a read-only scope.
 */
export const ReadOnly = ({ children, when }: { children: ReactNode; when: boolean }) => {
  const outer = useContext(ReadOnlyContext)

  return <ReadOnlyContext value={outer || when} data-sentry-element="ReadOnlyContext" data-sentry-component="ReadOnly" data-sentry-source-file="readonly.tsx">{children}</ReadOnlyContext>
}

/** Whether an editor renders read-only: its own `readOnly` prop or an enclosing `ReadOnly` scope. */
export const useReadOnly = (readOnly?: boolean) => useContext(ReadOnlyContext) || Boolean(readOnly)

export const ReadOnlyTooltip = ({
  children,
  side = 'right',
  tooltipContent,
}: {
  children: ReactNode
  side?: ComponentProps<typeof TooltipContent>['side']
  tooltipContent?: string
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false)

  return (
    <FieldContent
      className="w-full"
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
    data-sentry-element="FieldContent" data-sentry-component="ReadOnlyTooltip" data-sentry-source-file="readonly.tsx">
      <Tooltip open={tooltipContent ? tooltipOpen : false} data-sentry-element="Tooltip" data-sentry-source-file="readonly.tsx">
        <TooltipTrigger
          render={
            <span className="min-h-6 w-fit max-w-full p-0 text-base text-pretty whitespace-pre-line" />
          }
        data-sentry-element="TooltipTrigger" data-sentry-source-file="readonly.tsx">
          {children}
        </TooltipTrigger>
        {tooltipContent && (
          <TooltipContent side={side}>
            <p className="max-w-64 text-sm text-balance">{tooltipContent}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </FieldContent>
  )
}

/** A Read-only field's value, under the editor's own label when the editor has one. */
export const ReadOnlyField = ({
  label,
  ...tooltip
}: ComponentProps<typeof ReadOnlyTooltip> & { label?: ReactNode }) =>
  label ? (
    <Field className="gap-1">
      {label}
      <ReadOnlyTooltip {...tooltip} />
    </Field>
  ) : (
    <ReadOnlyTooltip {...tooltip} />
  )
